<template>
  <iColumn gap="standard" width="fill">
    <iColumn
      v-for="(tier, tierIndex) in tiers"
      :key="tierIndex"
      gap="standard"
      width="fill"
    >
      <iCard width="fill">
        <template #body>
          <iColumn gap="standard" width="fill">
            <iRow align="between" vertical-align="middle">
              <iText font-size="small" variant="branded">
                TIER {{ tierIndex + 1 }}
              </iText>
              <iButton
                v-if="tiers.length > 1"
                variant="tertiary"
                size="small"
                width="hug"
                @click="removeTier(tierIndex)"
              >
                Remove
              </iButton>
            </iRow>

            <template v-for="tierField in field.tier_fields" :key="tierField.key">
              <iColumn
                v-if="tierField.type !== FIELD_TYPE_TEXTAREA && !isGroupedField(tierField)"
                gap="extraSmall"
                width="fill"
              >
                <iText :variant="tierFieldLabelVariant(tierIndex, tierField)">
                  {{ tierFieldLabelWithHint(tierField) }}
                </iText>
                <iTextInput
                  :model-value="tier[tierField.key]"
                  :placeholder="tierField.placeholder || ''"
                  width="fill"
                  @update:model-value="updateTierFieldValue(tierIndex, tierField.key, $event)"
                />
              </iColumn>
              <iColumn
                v-else-if="tierField.type === FIELD_TYPE_TEXTAREA"
                gap="extraSmall"
                width="fill"
              >
                <iText :variant="tierFieldLabelVariant(tierIndex, tierField)">
                  {{ tierFieldLabelWithHint(tierField) }}
                </iText>
                <iTextArea
                  :model-value="tier[tierField.key]"
                  :placeholder="tierField.placeholder || ''"
                  :rows="tierField.textarea_rows || 6"
                  width="fill"
                  @update:model-value="updateTierFieldValue(tierIndex, tierField.key, $event)"
                />
              </iColumn>
            </template>

            <iRow
              v-for="groupKey in groupedFieldKeys"
              :key="`group-${groupKey}`"
              gap="standard"
              wrap="wrap"
            >
              <iColumn
                v-for="tierField in fieldsInGroup(groupKey)"
                :key="tierField.key"
                :min-width="200"
                width="fill"
                gap="extraSmall"
              >
                <iText :variant="groupedFieldLabelVariant(tierIndex, groupKey)">
                  {{ tierFieldLabelWithHint(tierField) }}
                </iText>
                <iTextInput
                  :model-value="tier[tierField.key]"
                  :placeholder="tierField.placeholder || ''"
                  width="fill"
                  @update:model-value="updateTierFieldValue(tierIndex, tierField.key, $event)"
                />
              </iColumn>
            </iRow>
          </iColumn>
        </template>
      </iCard>
    </iColumn>

    <iButton variant="secondary" width="fill" @click="addTier">
      + Add another tier
    </iButton>
  </iColumn>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useQuestionnaireStore } from "@/stores/questionnaire-store";
import { FIELD_TYPE_TEXTAREA } from "%/questionnaire/schema-helpers";

export default {
  name: "QuestionnaireSubscriptionTiers",
  props: {
    sectionKey: {
      type: String,
      required: true,
    },
    field: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      FIELD_TYPE_TEXTAREA,
    };
  },
  computed: {
    ...mapState(useQuestionnaireStore, [
      "responseData",
      "hasAttemptedToAdvance",
      "missingFieldKeysForCurrentStep",
    ]),
    tiers() {
      return this.responseData[this.sectionKey][this.field.key] || [];
    },
    groupedFieldKeys() {
      const keys = new Set();
      for (const tierField of this.field.tier_fields) {
        if (tierField.required_one_of_group) {
          keys.add(tierField.required_one_of_group);
        }
      }
      return Array.from(keys);
    },
  },
  methods: {
    ...mapActions(useQuestionnaireStore, [
      "addSubscriptionTier",
      "removeSubscriptionTier",
      "setSingleSelectValue",
    ]),
    isGroupedField(tierField) {
      return tierField.required_one_of_group != null;
    },
    tierFieldLabelWithHint(tierField) {
      if (tierField.hint) {
        return `${tierField.label} (${tierField.hint})`;
      }
      return tierField.label;
    },
    fieldsInGroup(groupKey) {
      return this.field.tier_fields.filter(tierField => tierField.required_one_of_group === groupKey);
    },
    updateTierFieldValue(tierIndex, tierFieldKey, newValue) {
      this.tiers[tierIndex][tierFieldKey] = newValue;
    },
    addTier() {
      this.addSubscriptionTier(this.sectionKey, this.field.key);
    },
    removeTier(tierIndex) {
      this.removeSubscriptionTier(this.sectionKey, this.field.key, tierIndex);
    },
    tierFieldLabelVariant(tierIndex, tierField) {
      if (!this.hasAttemptedToAdvance) {
        return "standard";
      }
      const key = `${this.sectionKey}.${this.field.key}[${tierIndex}].${tierField.key}`;
      return this.missingFieldKeysForCurrentStep.includes(key) ? "error" : "standard";
    },
    groupedFieldLabelVariant(tierIndex, groupKey) {
      if (!this.hasAttemptedToAdvance) {
        return "standard";
      }
      const key = `${this.sectionKey}.${this.field.key}[${tierIndex}].${groupKey}`;
      return this.missingFieldKeysForCurrentStep.includes(key) ? "error" : "standard";
    },
  },
};
</script>
